import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";

function hasActiveChild(children = [], pathname = "") {
  return children.some((child) => {
    if (child?.route_path && pathname.startsWith(child.route_path)) {
      return true;
    }

    if (Array.isArray(child?.children) && child.children.length > 0) {
      return hasActiveChild(child.children, pathname);
    }

    return false;
  });
}

function MenuDot({ active = false }) {
  return (
    <span
      className={`payroll-sidebar__item-icon ${active ? "is-active" : ""}`}
      aria-hidden="true"
    />
  );
}

MenuDot.propTypes = {
  active: PropTypes.bool,
};

export default function SidebarItem({ item, level = 0 }) {
  const location = useLocation();
  const children = Array.isArray(item?.children) ? item.children : [];
  const routePath = item?.route_path?.trim() || null;

  const nestedActive = useMemo(
    () => hasActiveChild(children, location.pathname),
    [children, location.pathname]
  );

  const [open, setOpen] = useState(nestedActive);

  useEffect(() => {
    if (nestedActive) {
      setOpen(true);
    }
  }, [nestedActive]);

  if (!item?.name) {
    return null;
  }

  const hasChildren = children.length > 0;
  const isDirectActive = routePath ? location.pathname.startsWith(routePath) : false;
  const isActive = isDirectActive || nestedActive;
  const paddingLeft = 14 + level * 14;

  if (hasChildren) {
    return (
      <div className={`payroll-sidebar__group ${open ? "is-open" : ""}`}>
        <button
          type="button"
          className={`payroll-sidebar__item payroll-sidebar__item--group ${
            isActive ? "is-active" : ""
          }`}
          style={{ paddingLeft: `${paddingLeft}px` }}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="payroll-sidebar__item-main">
            <span className="payroll-sidebar__item-rail" />
            <MenuDot active={isActive} />
            <span className="payroll-sidebar__item-label">{item.name}</span>
          </span>

          <span className={`payroll-sidebar__chevron ${open ? "is-open" : ""}`}>
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M6 8l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        <div className={`payroll-sidebar__children ${open ? "is-open" : ""}`}>
          {children.map((child, index) => (
            <SidebarItem
              key={child?.code || child?.id || child?.route_path || `${item.name}-${index}`}
              item={child}
              level={level + 1}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!routePath) {
    return (
      <div
        className="payroll-sidebar__item payroll-sidebar__item--muted"
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        <span className="payroll-sidebar__item-main">
          <span className="payroll-sidebar__item-rail" />
          <MenuDot />
          <span className="payroll-sidebar__item-label">{item.name}</span>
        </span>
      </div>
    );
  }

  return (
    <NavLink
      to={routePath}
      className={({ isActive: navActive }) =>
        `payroll-sidebar__item ${navActive ? "is-active" : ""}`
      }
      style={{ paddingLeft: `${paddingLeft}px` }}
    >
      {({ isActive: navActive }) => (
        <span className="payroll-sidebar__item-main">
          <span className="payroll-sidebar__item-rail" />
          <MenuDot active={navActive} />
          <span className="payroll-sidebar__item-label">{item.name}</span>
        </span>
      )}
    </NavLink>
  );
}

SidebarItem.propTypes = {
  item: PropTypes.shape({
    code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    route_path: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.object),
  }),
  level: PropTypes.number,
};
